import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { Badge } from "~/atoms/badge";
import { CenterSpinner } from "~/atoms/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/atoms/table";
import { useDocuments } from "~/api/v2/useDocuments";
import { DashboardCard } from "~/molecules/DashboardCard";
import ErrorState from "~/molecules/ErrorState";
import { needsCategorization } from "~/helpers/documentPermissions";

/**
 * Shared documents the receiving unit has yet to place in its framework.
 * Only superadmins and coordinators can see copies without a category, so
 * only they get this container, and it stays hidden while there are none.
 */
export const SharedDocumentsToCategorizeContainer = () => {
  const { t } = useTranslation();
  const { data, isLoading, error } = useDocuments();
  const documents = useMemo(() => data?.filter(needsCategorization), [data]);

  const title = t(($) => $.dashboard.sharedDocuments.title);

  if (isLoading) {
    return (
      <DashboardCard title={title}>
        <CenterSpinner />
      </DashboardCard>
    );
  }

  if (error || documents === undefined) {
    return (
      <DashboardCard title={title}>
        <ErrorState />
      </DashboardCard>
    );
  }

  if (documents.length === 0) return null;

  return (
    <DashboardCard title={title}>
      <p className="text-muted-foreground pb-3 text-sm">
        {t(($) => $.dashboard.sharedDocuments.description)}
      </p>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t(($) => $.commons.document.title)}</TableHead>
            <TableHead>{t(($) => $.commons.document.sharedFrom)}</TableHead>
            <TableHead>
              {t(($) => $.dashboard.sharedDocuments.missing)}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {documents.map((document) => (
            <TableRow key={document.id}>
              <TableCell>
                <Link
                  to={`/documents/${document.id}?tab=metadata`}
                  className="font-medium hover:underline"
                >
                  {document.name}
                </Link>
              </TableCell>
              <TableCell>{document.sharedFrom?.governanceUnit.name}</TableCell>
              <TableCell>
                <div className="flex gap-1">
                  {document.category === null && (
                    <Badge variant="warn">
                      {t(($) => $.commons.document.category)}
                    </Badge>
                  )}
                  {document.tier === null && (
                    <Badge variant="warn">
                      {t(($) => $.commons.document.tier)}
                    </Badge>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </DashboardCard>
  );
};
